import { useMemo, useState } from "react";

import { normalizeTraceResponseMessage } from "@/components/RequestDisplay/ChatView/utils";
import {
  RequestDisplayData,
  RequestDisplaySource,
  getChatRequestDisplayState,
  getJsonSchemaForRequestDisplay,
  isMalformedMessage,
} from "@/components/RequestDisplay/RequestDisplayUtils";
import { LOCAL_STORAGE } from "@/constants";
import useLocalStorageState from "@/hooks/useLocalStorageState";
import { ChatTemplate, PromptBlueprint } from "@/types";
import { truncateBase64InObject } from "@/utils/strings";
import { getStringContent } from "@/utils/utils";

const getDisplayError = (
  requestInfo: RequestDisplayData,
  promptTemplate: PromptBlueprint | null,
) => {
  try {
    const template = promptTemplate?.prompt_template;
    if (template?.type === "chat") {
      if (getChatRequestDisplayState(template).allMessages.some(isMalformedMessage)) {
        return { hasError: true, title: "Malformed Request" };
      }
    }

    return { hasError: false, title: "" };
  } catch (error) {
    console.error("Error processing request display:", error); // eslint-disable-line no-console
    return { hasError: true, title: "Error Displaying Request" };
  }
};

export const useRequestDisplayState = ({
  requestBulk,
  requestId,
  requestPreview,
}: {
  requestId: string;
  requestPreview?: RequestDisplaySource;
  requestBulk?: RequestDisplaySource;
}) => {
  const requestInfo = useMemo(
    () => (requestPreview ? ({ ...requestPreview, ...requestBulk } as RequestDisplayData) : null),
    [requestBulk, requestPreview],
  );

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showFullRequest, setShowFullRequest] = useState(false);
  const [showFullResponse, setShowFullResponse] = useState(false);
  const [choice, setChoice] = useState(0);
  const [requestDisplayView, setRequestDisplayView] = useLocalStorageState<"template" | "chat">(
    LOCAL_STORAGE.REQUEST_DISPLAY_VIEW,
    "chat",
  );
  const [chatBoxOpenStates, setChatBoxOpenStates] = useState<Record<string, boolean | undefined>>(
    {},
  );

  const handleCollapseAll = () => {
    const newRecord = Object.fromEntries(
      Object.keys(chatBoxOpenStates).map(key =>
        key.startsWith(requestId) ? [key, false] : [key, chatBoxOpenStates[key]],
      ),
    );
    setChatBoxOpenStates(newRecord);
  };

  const handleExpandAll = () => {
    const newRecord = Object.fromEntries(
      Object.keys(chatBoxOpenStates).map(key =>
        key.startsWith(requestId) ? [key, true] : [key, chatBoxOpenStates[key]],
      ),
    );
    setChatBoxOpenStates(newRecord);
  };

  const promptBlueprint: PromptBlueprint | null = requestInfo?.prompt_version ?? null;
  const template = promptBlueprint?.prompt_template;
  const chatDisplayState = useMemo(
    () => (template?.type === "chat" ? getChatRequestDisplayState(template) : null),
    [template],
  );
  const requestJson = useMemo(
    () => ({
      tags: requestInfo?.tags_array,
      function_name: requestInfo?.function_name,
      function_args: requestInfo?.function_args,
      function_kwargs: requestInfo?.function_kwargs,
      request_start_time: requestInfo?.request_start_time,
      request_end_time: requestInfo?.request_end_time,
      json_schema: getJsonSchemaForRequestDisplay(
        requestInfo?.prompt_version?.metadata?.model?.parameters as
          | Record<string, unknown>
          | undefined,
        requestInfo?.function_kwargs as Record<string, unknown> | undefined,
      ),
    }),
    [requestInfo],
  );

  const requestText = useMemo(() => {
    if (!requestInfo) {
      return "";
    }

    const shortRequestText =
      template?.type === "chat"
        ? (chatDisplayState?.requestMessages ?? [])
            .map(message => `Role: ${message.role}\nContent: ${getStringContent(message)}\n`)
            .join("\n")
        : requestInfo.prompt_string;

    if (!showFullRequest) {
      return shortRequestText;
    }

    return JSON.stringify(
      truncateBase64InObject(requestJson || requestInfo.request_json_fallback),
      undefined,
      2,
    );
  }, [chatDisplayState?.requestMessages, requestInfo, requestJson, showFullRequest, template]);

  const responseText = useMemo(() => {
    if (!requestInfo) {
      return "";
    }

    let shortResponseText = requestInfo.response_string;
    if (requestInfo.is_embedding) {
      shortResponseText = "[Generated Embedding]";
    } else if (template?.type === "chat") {
      const responseMessage = chatDisplayState?.responseMessage;
      if (responseMessage) {
        shortResponseText = `Role: ${
          responseMessage.role
        }\nContent: ${getStringContent(responseMessage)}\n`;
      }
    }

    if (!showFullResponse) {
      return shortResponseText;
    }

    return JSON.stringify(
      truncateBase64InObject(
        requestInfo.response_json ||
          requestInfo.response_json_fallback ||
          requestInfo.request_response,
      ),
      undefined,
      2,
    );
  }, [chatDisplayState?.responseMessage, requestInfo, showFullResponse, template]);

  const jsonSchemaRaw = requestJson?.json_schema;
  const jsonSchema = jsonSchemaRaw?.json_schema ? jsonSchemaRaw : undefined;

  const isChat = template?.type === "chat";
  const isImage = Boolean(requestInfo?.is_image);
  const isAudio = Boolean(requestInfo?.is_audio);
  const isVideo = Boolean(requestInfo?.is_video);
  // Audio (TTS) and video output are rendered with the same media renderer as images.
  const isMedia = isImage || isAudio || isVideo;
  const isChatView = Boolean(isChat && requestDisplayView === "chat");
  const requestResponse = requestInfo?.request_response;
  const chatResponseMessage = chatDisplayState?.responseMessage;
  const isEmptyResponse = chatResponseMessage && !chatDisplayState?.hasResponseContent;
  const hasResponseForChat = isChat && Boolean(chatResponseMessage) && !isEmptyResponse;
  const hasResponseForImage = isMedia && Boolean(requestResponse);
  const hasResponseForCompletion =
    !isChat &&
    !isMedia &&
    Boolean(requestInfo?.response_string || requestInfo?.is_embedding || requestResponse);
  const hasNoResponse = !(hasResponseForChat || hasResponseForImage || hasResponseForCompletion);

  const displayError = requestInfo
    ? getDisplayError(requestInfo, promptBlueprint)
    : { hasError: false, title: "" };

  const choicesCount = useMemo(() => {
    if (
      !requestInfo ||
      requestInfo.response_json?.error ||
      !requestInfo.request_response ||
      !requestInfo.request_response?.choices
    ) {
      return 0;
    }

    return requestInfo.request_response.choices.length;
  }, [requestInfo]);

  const choiceResponse = useMemo(() => {
    if (!requestInfo || choicesCount <= 1) {
      return null;
    }

    if (isChat) {
      return requestInfo.request_response.choices[choice].message;
    }

    return requestInfo.request_response.choices[choice].text;
  }, [choice, choicesCount, isChat, requestInfo]);

  const choiceResponseText = useMemo(() => {
    if (!choiceResponse || !requestInfo) {
      return "";
    }

    if (isChat) {
      return JSON.stringify(truncateBase64InObject(choiceResponse), undefined, 2);
    }

    return JSON.stringify(
      truncateBase64InObject(requestInfo.response_json || choiceResponse),
      undefined,
      2,
    );
  }, [choiceResponse, isChat, requestInfo]);

  const responseMessageOverride =
    choiceResponse && template?.type === "chat"
      ? normalizeTraceResponseMessage(
          choiceResponse,
          (template as ChatTemplate).messages[0]?.template_format ?? "f-string",
        )
      : null;

  const chatResponseTextBlock = useMemo(() => {
    if (!choiceResponse) {
      return responseText;
    }

    if (showFullResponse || isChat) {
      return choiceResponseText;
    }

    return choiceResponse;
  }, [choiceResponse, choiceResponseText, isChat, responseText, showFullResponse]);

  const fullScreenModalText = useMemo(() => {
    if (showRequestModal) {
      return requestText;
    }

    if (choiceResponse) {
      if (isChat && !showFullResponse) {
        return `Role: ${choiceResponse?.role}\nContent: ${choiceResponse?.content}`;
      }

      return chatResponseTextBlock;
    }

    return responseText;
  }, [
    chatResponseTextBlock,
    choiceResponse,
    isChat,
    requestText,
    responseText,
    showFullResponse,
    showRequestModal,
  ]);

  const bulkLoaded = Boolean(requestBulk);

  const handleToggleRequestDisplayView = () => {
    setRequestDisplayView(current => (current === "chat" ? "template" : "chat"));
  };

  const usage = requestInfo?.request_response?.usage;

  return {
    bulkLoaded,
    chatBoxOpenStates,
    chatResponseTextBlock,
    choice,
    choicesCount,
    choiceResponse,
    displayError,
    fullScreenModalText,
    handleCollapseAll,
    handleExpandAll,
    handleToggleRequestDisplayView,
    hasNoResponse,
    cacheCreationInputTokens: usage?.cache_creation_input_tokens ?? usage?.cacheWriteInputTokens,
    cacheReadInputTokens: usage?.cache_read_input_tokens ?? usage?.cacheReadInputTokens,
    inputTokens: requestInfo?.input_tokens,
    isChat,
    isChatView,
    isImage,
    isMedia,
    jsonSchema,
    outputTokens: requestInfo?.output_tokens,
    requestInfo,
    requestResponse,
    requestText,
    responseMessageOverride,
    setChatBoxOpenStates,
    setChoice,
    setShowRequestModal,
    setShowFullRequest,
    setShowFullResponse,
    setShowResponseModal,
    showFullRequest,
    showFullResponse,
    showRequestModal,
    showResponseModal,
    template,
    engine: requestInfo?.engine,
  };
};
